/**
 * ssiOverrideAudit
 *
 * Audit-trail helpers for the SLaM / 3 Borough "Lead SSI takeover" flow.
 *
 * Background: when a Lead clinician takes over a completed Semi-Structured
 * Interview (SSI), they can change the original clinician's symptom / domain
 * decisions and then "recomplete" the module from the Review & Synthesis page.
 * The takeover *start* is already audited by the backend (assignmentHistory +
 * the `leadOverride` record), and the original clinician's full decisions are
 * preserved in the `ssiOverrideArchive/{archiveId}` snapshot. What was missing
 * was a discrete, easily-queried record of the Lead's *recompletion* and a
 * compact summary of *what they changed*.
 *
 * This module provides:
 *   1. Pure, testable diff helpers that compare the "before" (archived) SSI
 *      decisions to the "after" (live) decisions and produce a compact summary.
 *   2. A fire-and-forget recorder that, on recompletion, writes a distinct
 *      audit event and stamps the summary onto the `leadOverride` record.
 *
 * Design constraints (match existing audit patterns in reportAuditHelpers.js):
 *   - Non-blocking: the recorder never throws to its caller and never delays
 *     navigation — it is called without `await` and swallows all errors.
 *   - No Firestore rules change: the discrete event reuses the existing
 *     `reportAuditHistory` subcollection (only a new `eventType` *value*, which
 *     the rules' key allowlist permits), and the summary is stamped inline onto
 *     the patient document's `leadOverride` record (which clinicians with
 *     patient access may already update).
 */

import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { appendReportAuditEntry } from './reportAuditHelpers';

// SSI proforma section titles. These must match the titles used throughout the
// SSI pages (see 3_SSIReview.js) so we read the right sections from both the
// live data model and the archived snapshot.
const SYMPTOMS_TITLE = 'Symptoms';
const DOMAINS_TITLE = 'Domains of Functional Impairment';

// A symptom's DSM code prefix tells us which criterion group it belongs to:
// A1.x = Inattention, A2.x = Hyperactivity / Impulsivity.
const INATTENTION_PREFIX = 'A1';
const HYPERACTIVITY_PREFIX = 'A2';

/**
 * Finds a section object by its `sectionTitle` within an SSI `sections` map.
 * The `sections` map is keyed by section number (e.g. '1', '2'), so we scan the
 * values. Returns null when the section is absent.
 *
 * @param {Object|undefined|null} sections  SSI module `sections` map
 * @param {string} title  The section title to find
 * @returns {Object|null}
 */
function findSection(sections, title) {
  if (!sections || typeof sections !== 'object') return null;
  return Object.values(sections).find((s) => s?.sectionTitle === title) || null;
}

// Normalise a decision flag to a strict boolean. SSI flags can be true / false
// / undefined; we treat anything other than an explicit `true` as "not set".
const asBool = (v) => v === true;

/**
 * Extracts a normalised decision map from an SSI `sections` object so that the
 * "before" (archive) and "after" (live) decisions can be compared with
 * identical semantics.
 *
 * @param {Object|undefined|null} sections  SSI module `sections` map
 * @returns {{ symptoms: Object, domains: Object }}
 *   symptoms: { [DSM_code]: { adulthood: boolean, childhood: boolean, group: 'inattention'|'hyperactivity'|'other' } }
 *   domains:  { [domainName]: { adulthood: boolean, childhood: boolean } }
 */
export function extractSSIDecisions(sections) {
  const symptoms = {};
  const domains = {};

  const symptomsSection = findSection(sections, SYMPTOMS_TITLE);
  if (symptomsSection?.questions) {
    Object.values(symptomsSection.questions).forEach((q) => {
      const code = q?.DSM_code;
      if (!code) return;
      let group = 'other';
      if (code.startsWith(INATTENTION_PREFIX)) group = 'inattention';
      else if (code.startsWith(HYPERACTIVITY_PREFIX)) group = 'hyperactivity';
      symptoms[code] = {
        adulthood: asBool(q.symptomPresent_Adulthood),
        childhood: asBool(q.symptomPresent_Childhood),
        group,
      };
    });
  }

  const domainsSection = findSection(sections, DOMAINS_TITLE);
  if (domainsSection?.questions) {
    Object.values(domainsSection.questions).forEach((q) => {
      const name = q?.DSM_symptom || q?.DSM_symptom_prefix;
      if (!name) return;
      domains[name] = {
        adulthood: asBool(q.functioningReduced_Adulthood),
        childhood: asBool(q.functioningReduced_Childhood),
      };
    });
  }

  return { symptoms, domains };
}

// Counts how many symptoms in a given group are marked present, split by
// adulthood / childhood.
function countGroup(symptoms, group) {
  const items = Object.values(symptoms).filter((s) => s.group === group);
  return {
    adulthood: items.filter((s) => s.adulthood).length,
    childhood: items.filter((s) => s.childhood).length,
  };
}

// Counts how many domains are marked impaired, split by adulthood / childhood.
function countDomains(domains) {
  const items = Object.values(domains);
  return {
    adulthood: items.filter((d) => d.adulthood).length,
    childhood: items.filter((d) => d.childhood).length,
  };
}

/**
 * Computes a compact summary of how the Lead's (after) SSI decisions differ
 * from the original clinician's (before) decisions.
 *
 * The summary is intentionally small and high-signal:
 *   - which symptom codes / domain names flipped (in either direction, for
 *     either adulthood or childhood),
 *   - before/after present-counts for each criterion group and for domains.
 *
 * @param {Object|undefined|null} beforeSections  Archived SSI `sections`
 * @param {Object|undefined|null} afterSections   Live SSI `sections`
 * @returns {{
 *   symptomsChanged: string[],
 *   domainsChanged: string[],
 *   counts: Object,
 *   hasChanges: boolean,
 * }}
 */
export function computeSSIDecisionChangeSummary(beforeSections, afterSections) {
  const before = extractSSIDecisions(beforeSections);
  const after = extractSSIDecisions(afterSections);

  // A symptom/domain is "changed" if either its adulthood or childhood flag
  // differs. We scan the union of keys so additions / removals are caught too.
  const changedKeys = (beforeMap, afterMap) => {
    const keys = new Set([...Object.keys(beforeMap), ...Object.keys(afterMap)]);
    const changed = [];
    keys.forEach((key) => {
      const b = beforeMap[key] || { adulthood: false, childhood: false };
      const a = afterMap[key] || { adulthood: false, childhood: false };
      if (b.adulthood !== a.adulthood || b.childhood !== a.childhood) {
        changed.push(key);
      }
    });
    // Stable, human-friendly ordering.
    return changed.sort((x, y) => x.localeCompare(y));
  };

  const symptomsChanged = changedKeys(before.symptoms, after.symptoms);
  const domainsChanged = changedKeys(before.domains, after.domains);

  const counts = {
    inattention: {
      adulthood: {
        before: countGroup(before.symptoms, 'inattention').adulthood,
        after: countGroup(after.symptoms, 'inattention').adulthood,
      },
      childhood: {
        before: countGroup(before.symptoms, 'inattention').childhood,
        after: countGroup(after.symptoms, 'inattention').childhood,
      },
    },
    hyperactivity: {
      adulthood: {
        before: countGroup(before.symptoms, 'hyperactivity').adulthood,
        after: countGroup(after.symptoms, 'hyperactivity').adulthood,
      },
      childhood: {
        before: countGroup(before.symptoms, 'hyperactivity').childhood,
        after: countGroup(after.symptoms, 'hyperactivity').childhood,
      },
    },
    domains: {
      adulthood: {
        before: countDomains(before.domains).adulthood,
        after: countDomains(after.domains).adulthood,
      },
      childhood: {
        before: countDomains(before.domains).childhood,
        after: countDomains(after.domains).childhood,
      },
    },
  };

  return {
    symptomsChanged,
    domainsChanged,
    counts,
    hasChanges: symptomsChanged.length > 0 || domainsChanged.length > 0,
  };
}

/**
 * Records a Lead-override SSI recompletion. Fire-and-forget: call WITHOUT
 * `await` so it never blocks navigation, and it swallows all errors so an audit
 * failure can never break the primary completion flow.
 *
 * It performs two independent, non-blocking writes:
 *   1. A distinct `reportAuditHistory` event (`eventType: 'ssiLeadOverrideRecomplete'`)
 *      so the recompletion shows up as its own timeline entry (separate from the
 *      generic 'generated' report event the finish handler also writes).
 *   2. Stamps `lastRecompletedAt/By/ByName` and a compact `decisionChangeSummary`
 *      onto the `leadOverride` record on the patient document. The "before"
 *      decisions come from the `ssiOverrideArchive/{archiveId}` snapshot; if the
 *      archive is missing/unreadable we still stamp the recompletion metadata
 *      and skip the diff summary.
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {string|undefined|null} params.archiveId  leadOverride.archiveId
 * @param {Object|undefined|null} params.liveSSISections  Current SSI `sections`
 */
export async function recordLeadOverrideRecompletion({ patientId, archiveId, liveSSISections }) {
  // (1) Distinct timeline event (already fire-and-forget internally).
  appendReportAuditEntry(patientId, {
    module: 'ssi',
    eventType: 'ssiLeadOverrideRecomplete',
    isFirstOccurrence: false,
  });

  // (2) Inline stamp on the leadOverride record + decision-change summary.
  try {
    const user = auth.currentUser;

    let summary = null;
    if (archiveId) {
      const archiveRef = doc(db, 'patients', patientId, 'ssiOverrideArchive', archiveId);
      const archiveSnap = await getDoc(archiveRef);
      const beforeSections = archiveSnap.exists()
        ? archiveSnap.data()?.previousSSIModule?.sections
        : null;
      summary = computeSSIDecisionChangeSummary(beforeSections, liveSSISections);
    }

    const updates = {
      'dataModel.3_semiStructuredInterview.leadOverride.lastRecompletedAt': serverTimestamp(),
      'dataModel.3_semiStructuredInterview.leadOverride.lastRecompletedBy': user?.uid ?? null,
      'dataModel.3_semiStructuredInterview.leadOverride.lastRecompletedByName': user?.displayName ?? null,
    };
    if (summary) {
      updates['dataModel.3_semiStructuredInterview.leadOverride.decisionChangeSummary'] = {
        ...summary,
        computedAt: serverTimestamp(),
      };
    }

    await updateDoc(doc(db, 'patients', patientId), updates);
  } catch (err) {
    // Non-blocking: log and move on. The primary completion write and the
    // distinct audit event above are unaffected.
    console.error('Failed to record Lead SSI override recompletion audit:', err);
  }
}
